import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Textarea,
  Button,
  Text,
  useToast,
  Flex,
  Avatar,
  Divider,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Checkbox,
  CheckboxGroup,
} from '@chakra-ui/react';
import { useAuth } from '../contexts/AuthContext';
import { getChatMessages, sendChatMessage, getUserProfile, getPersonalDetail, getUIDNameTable, readChat } from '../services/api';
import chat_img from '../assets/chat_img.jpg';

const SODAN_OPTIONS = [
  {
    label: '3D上でどんな見栄えになるかが知りたい',
    message: '3D上でどんな見栄えになるかが知りたいです。',
  },
  {
    label: 'どんな形状・配置が良いか知りたい',
    message: 'どんな形状・配置が良いか知りたいです。',
  },
  {
    label: 'どうやって選んだらよいかわからない',
    message: 'どうやって選んだらよいかわかりません。',
  },
  {
    label: 'こだわりがないので、高コスパ商品を選びたい',
    message: 'こだわりがないので、高コスパ商品を選びたいです。',
  },
  {
    label: 'このアプリの使い方を教えてほしい',
    message: 'このアプリの使い方を教えてください。',
  },
  {
    label: '土地探しで困っている',
    message: '土地探しで困っています。',
  }
];

const ChatBox = ({ itemType, targetUserId, isOpen }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const { currentUser } = useAuth();
  const toast = useToast();
  const messagesEndRef = useRef(null);
  const chatBoxRef = useRef(null);
  const [chatReady, setChatReady] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState(null);
  const [uidNameTable, setUIDNameTable] = useState({});
  const [sodanModalOpen, setSodanModalOpen] = useState(false);
  const [selectedSodan, setSelectedSodan] = useState([]);
  const [readAll, setReadAll] = useState(false);
  const [hasNewMessages, setHasNewMessages] = useState(false);
  const [isReadingHistory, setIsReadingHistory] = useState(false);

  const handleScroll = () => {
    if (!chatBoxRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = chatBoxRef.current;
    // スクロールが最下部に近づいたら（50px以内）既読
    if (scrollHeight - scrollTop - clientHeight < 50) {
      setIsReadingHistory(false);
      if (!readAll) {
        readChat(itemType, targetUserId || currentUser.uid);
        setReadAll(true);
        setHasNewMessages(false);
      }
    } else {
      setIsReadingHistory(true);
    }
  };

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 30000);
    return () => clearInterval(interval);
  }, [itemType, targetUserId]);

  useEffect(() => {
    if (!isReadingHistory && isOpen) {
      scrollToBottom();
    }
  }, [messages, isOpen]);

  useEffect(() => {
    // ユーザープロフィール取得
    const fetchProfile = async () => {
      setProfileLoading(true);
      setProfileError(null);
      try {
        const userId = targetUserId || currentUser.uid;
        const data = await getUserProfile(userId);
        const personalData = await getPersonalDetail(userId);
        setChatReady(data.name && data.phone && data.address && personalData.nickname);
      } catch (e) {
        setProfileError('ユーザー情報の取得に失敗しました');
      } finally {
        setProfileLoading(false);
      }
    };
    fetchProfile();
  }, [targetUserId, currentUser]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    // JST（日本標準時）に変換
    const jstDate = new Date(date.getTime() + 9 * 60 * 60 * 1000);
    const month = (jstDate.getMonth() + 1).toString().padStart(2, '0');
    const day = jstDate.getDate().toString().padStart(2, '0');
    const hours = jstDate.getHours().toString().padStart(2, '0');
    const minutes = jstDate.getMinutes().toString().padStart(2, '0');
    return `${month}/${day} ${hours}:${minutes}`;
  };

  const scrollToBottom = () => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  };
  
  const fetchMessages = async () => {
    try {
      const data = await getChatMessages(itemType, targetUserId || currentUser.uid);
      if(data.length > messages.length) {
        setHasNewMessages(true);
        setReadAll(false);
        setMessages(data);
      }
    } catch (error) {
      console.error('メッセージ取得エラー:', error);
    }
    // ここで既読判定
    setTimeout(() => {
      if (chatBoxRef.current) {
        const { scrollHeight, clientHeight } = chatBoxRef.current;
        if (scrollHeight <= clientHeight && !readAll) {
          readChat(itemType, targetUserId || currentUser.uid);
          setReadAll(true);
          setHasNewMessages(false);
        }
      }
    }, 0); // DOM更新後に判定
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    setLoading(true);
    try {
      scrollToBottom();
      await sendChatMessage(targetUserId || currentUser.uid, itemType, newMessage);
      setNewMessage('');
      await fetchMessages();
      scrollToBottom();
    } catch (error) {
      toast({
        title: 'エラー',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    // Ctrl + Enter または Command + Enter で送信
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      if (newMessage.trim() && !loading) {
        sendMessage();
      }
    }
  };

  const fetchUIDNameTable = async () => {
    try {
      const uidNameTable = await getUIDNameTable();
      setUIDNameTable(uidNameTable);
    } catch (e) {
      console.error('UID名簿取得エラー:', e);
    } 
  };
  useEffect(() => {
    fetchUIDNameTable();
  }, [currentUser]);

  if (profileLoading) return <Text>ユーザー情報を確認中...</Text>;
  if (profileError) return <Text color="red.500">{profileError}</Text>;

  if (!chatReady) {
    // プロフィール未登録 or 編集モード
    return (
      <Box borderWidth="1px" borderRadius="lg" p={4}>
        <Text mb={2} fontWeight="bold">チャット利用にはプロフィール登録が必要です</Text>
        <Text mb={2} fontWeight="bold">ユーザ登録画面にて、氏名・電話番号・住所・表示名を入力してください</Text>        
        <br></br>
        <Text mb={2} fontWeight="bold">チャットイメージ</Text>
        <img src={chat_img} alt="チャットプレビュー" style={{ width: '100%', borderRadius: '8px', marginBottom: '12px' }} />
      </Box>
    );
  }

  return (
    <>
      <Box
        borderRadius="lg"
        p={0}
        h="100%"
        display="flex"
        flexDirection="column"
        position="relative"
      >
        <Box
          w={{base: "100%", sm: "400px"}}          
          transition="width 0.3s ease-in-out"
          overflow="hidden"
          display={"flex"}
          flexDirection="column"
        >
            {isReadingHistory && hasNewMessages && (
              <Button onClick={scrollToBottom} bg="yellow.500" color="white" p={2} borderRadius="lg" mb={4} alignSelf="center">↓新着メッセージ</Button>
            )}

          <VStack
            flex="1"
            overflowY="auto"
            spacing={4}
            align="stretch"
            mb={4}
            ref={chatBoxRef}
            onScroll={handleScroll}
          >
           { messages.map((message) => (
              <Box
                key={message.id}
                alignSelf={message.from_user_id == currentUser.uid ? 'flex-end' : 'flex-start'}
                maxW="70%"
              >
                <VStack spacing={1} align="flex-start">
                  {message.from_user_id != currentUser.uid && (
                    <Text fontSize="sm" color="gray.500">{uidNameTable[message.from_user_id]}</Text>
                  )}
                  <Box
                    bg={message.from_user_id == currentUser.uid ? 'black' : 'gray.100'}
                    color={message.from_user_id == currentUser.uid ? 'white' : 'black'}
                    p={3}
                    borderRadius="lg"
                  >
                    <Text whiteSpace="pre-wrap">{message.message}</Text>
                    <Text fontSize="xs" color={message.from_user_id != currentUser.uid ? 'whiteAlpha.1000' : 'gray.100'}>
                      {formatDate(message.created_at)}
                    </Text>
                  </Box>
                </VStack>
              </Box>
            ))}          
            <div ref={messagesEndRef} />
          </VStack>
          <Divider mb={4} />
          <VStack align="flex-start">
            <Textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="メッセージを入力... (Ctrl + Enterで送信)"
              disabled={loading}
              resize="none"
              rows={3}
            />
            <HStack w="100%">
              <Button
                onClick={sendMessage}
                isLoading={loading}
                disabled={!newMessage.trim()}
              >
                送信
              </Button>
              <Button
                bg="black"
                color="white"
                variant="outline"
                onClick={() => setSodanModalOpen(true)}
              >
                相談
              </Button>
            </HStack>
            <Text fontSize="xs" color="gray.500">※相談メッセージは、業務時間内で対応するため、回答が遅れる場合があります。</Text>
          </VStack>
        </Box>
      </Box>
      {/* 相談モーダル */}
      <Modal isOpen={sodanModalOpen} onClose={() => setSodanModalOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>相談したい内容を選択してください</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <CheckboxGroup
              value={selectedSodan}
              onChange={setSelectedSodan}
            >
              <VStack align="start">
                {SODAN_OPTIONS.map(opt => (
                  <Checkbox key={opt.label} value={opt.label}>{opt.label}</Checkbox>
                ))}
              </VStack>
            </CheckboxGroup>
          </ModalBody>
          <ModalFooter>
            <Button
              bg="black"
              color="white"
              mr={3}
              onClick={async () => {
                for (const opt of SODAN_OPTIONS) {
                  if (selectedSodan.includes(opt.label)) {
                    await sendChatMessage(targetUserId || currentUser.uid, itemType, opt.message);
                  }
                }
                setSodanModalOpen(false);
                setSelectedSodan([]);
                await fetchMessages();
              }}
              disabled={selectedSodan.length === 0}
            >
              送信
            </Button>
            <Button variant="ghost" onClick={() => setSodanModalOpen(false)}>キャンセル</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ChatBox; 